import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHotelManualFaq } from "@/hooks/useHotelManualFaq";
import { useHotelManualInfo } from "@/hooks/useHotelManualInfo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Card, CardContent } from "@/components/ui/card";
import { HelpCircle, ChevronDown } from "lucide-react";
import { buildAutoFaqItems, type AutoFaqItem } from "@/utils/hotelFaq";

interface HotelStaticFAQProps {
  hotelId: string;
  hotelName: string;
  destinationName?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  phone?: string | null;
  amenityNames?: string[];
}

export const HotelStaticFAQ = ({
  hotelId,
  hotelName,
  destinationName,
  address,
  city,
  country,
  phone,
  amenityNames = [],
}: HotelStaticFAQProps) => {
  const { t } = useTranslation();
  const { faqItems, getLocalizedQuestion, getLocalizedAnswer } =
    useHotelManualFaq(hotelId);
  const { manualInfo } = useHotelManualInfo(hotelId);

  // Replace {{hotelName}} placeholder in manual FAQ content
  const interpolateHotelName = (text: string) =>
    text.replace(/\{\{hotelName\}\}/g, hotelName);

  const manualItems: AutoFaqItem[] = useMemo(
    () =>
      faqItems.map((item) => ({
        id: `manual-${item.id}`,
        category: t("hotelFaq.categories.general"),
        question: interpolateHotelName(getLocalizedQuestion(item)),
        answer: interpolateHotelName(getLocalizedAnswer(item)),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [faqItems, getLocalizedQuestion, getLocalizedAnswer, hotelName, t],
  );

  const autoItems = useMemo(
    () =>
      buildAutoFaqItems({
        hotelName,
        destinationName,
        address,
        city,
        country,
        phone,
        amenityNames,
        isPetFriendly: manualInfo?.is_pet_friendly ?? null,
        t,
      }),
    [
      hotelName,
      destinationName,
      address,
      city,
      country,
      phone,
      amenityNames,
      manualInfo?.is_pet_friendly,
      t,
    ],
  );

  const allItems = useMemo(
    () => [...manualItems, ...autoItems],
    [manualItems, autoItems],
  );

  if (allItems.length === 0) return null;

  return (
    <Card>
      <Collapsible defaultOpen={false}>
        <CollapsibleTrigger className="w-full">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <HelpCircle className="h-5 w-5 text-primary" />
                </div>
                <div className="text-left">
                  <h2 className="text-xl font-semibold">
                    {t("hotelDetail.faq")}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {t("hotelFaq.questionsCount", { count: allItems.length })}
                  </p>
                </div>
              </div>
              <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </div>
          </CardContent>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-6 pb-6">
            <Accordion type="single" collapsible className="w-full">
              {allItems.map((item) => (
                <AccordionItem key={item.id} value={item.id}>
                  <AccordionTrigger className="text-left hover:no-underline py-4">
                    <span className="text-sm font-medium pr-4">
                      {item.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
